import type { IconName } from "src/components";
import {
  FileCategoryEnum,
  MailStatusEnum,
  CustomerPortalFileCategoryEnum,
} from "src/graphql/generated/types";

export const DocumentCategoryLabel: {
  [key in CustomerPortalFileCategoryEnum]: string;
} = {
  [CustomerPortalFileCategoryEnum.Sinister]: "Sinistre",
  [CustomerPortalFileCategoryEnum.Litigation]: "Contentieux",
  [CustomerPortalFileCategoryEnum.AccountSituation]:
    "Situation de compte à date",
  [CustomerPortalFileCategoryEnum.Insurance]: "Assurance",
  [CustomerPortalFileCategoryEnum.ProvisionCalls]: "Appels de provisions",
  [CustomerPortalFileCategoryEnum.Repair]: "Dépannage et réparation",
  [CustomerPortalFileCategoryEnum.Correspondence]: "Correspondances",
  [CustomerPortalFileCategoryEnum.LessorCoOwnershipDocuments]:
    "Documents de ma copropriété [BRAND_NAME]",
  [CustomerPortalFileCategoryEnum.MissionPropertyRepair]: "Mission Travaux",
  [CustomerPortalFileCategoryEnum.BoilerCertificate]:
    "Attestation d'entretien du système de chauffage",
  [CustomerPortalFileCategoryEnum.BankReconciliation]:
    "Relevés des comptes bancaires",
  [CustomerPortalFileCategoryEnum.TrusteeContract]: "Contrat de syndic",
  [CustomerPortalFileCategoryEnum.Deposit]: "Acomptes versés",
  [CustomerPortalFileCategoryEnum.DiagnosesGl]: "Diagnostics",
  [CustomerPortalFileCategoryEnum.DueNotice]:
    "Avis d’échéance, Quittance de loyer",
  [CustomerPortalFileCategoryEnum.GeneralAssembly]: "Assemblées générales",
  [CustomerPortalFileCategoryEnum.PeriodGeneralAssembly]:
    "Assemblées générales par période comptable",
  [CustomerPortalFileCategoryEnum.LandlordPropertyTaxes]: "Taxes foncières",
  [CustomerPortalFileCategoryEnum.HouseholdWasteTax]:
    "Taxe d’enlèvement des ordures ménagères",
  [CustomerPortalFileCategoryEnum.CoOwnershipIdentificationNumber]:
    "Immatriculation",
  [CustomerPortalFileCategoryEnum.Invoices]: "Factures",
  [CustomerPortalFileCategoryEnum.MaintenanceBook]: "Carnet d'entretien",
  [CustomerPortalFileCategoryEnum.LandIncomes]:
    "Déclarations de revenus fonciers",
  [CustomerPortalFileCategoryEnum.Leases]: "Bail",
  [CustomerPortalFileCategoryEnum.OperatingLeaseReport]:
    "Comptes rendus de gestion",
  [CustomerPortalFileCategoryEnum.RcpSettlement]: "Règlement de copropriété",
  [CustomerPortalFileCategoryEnum.RegularizationRentalManagement]:
    "Régularisation de charges locatives",
  [CustomerPortalFileCategoryEnum.ExternalExpenseRegularization]:
    "Documents de ma copropriété",
  [CustomerPortalFileCategoryEnum.StatementGeneralExpenses]:
    "Relevé général des dépenses",
  [CustomerPortalFileCategoryEnum.RentReview]: "Révision de loyer",
  [CustomerPortalFileCategoryEnum.ExpensesRepartition]:
    "Bilan annuel des charges",
  [CustomerPortalFileCategoryEnum.CouncilMeetingReport]:
    "Comptes rendus des réunions du conseil syndical",
  [CustomerPortalFileCategoryEnum.ReportVisit]:
    "Comptes rendus de visite d’immeuble",
  [CustomerPortalFileCategoryEnum.ResidentialRentalInspection]:
    "État des lieux",
  [CustomerPortalFileCategoryEnum.TenantStatementOfAccount]:
    "Arrêté de compte locataire",
  [CustomerPortalFileCategoryEnum.TenantSettlementAdditional]:
    "Arrêté de compte locataire complémentaire",
  [CustomerPortalFileCategoryEnum.Mandate]: "Mandat de prélèvement",
  [CustomerPortalFileCategoryEnum.MandateDebit]: "Mandat de prélèvement",
  [CustomerPortalFileCategoryEnum.ServiceOrder]: "Ordre de service",
  [CustomerPortalFileCategoryEnum.ElectroRegistredMailSubscription]: "E-reco",
  [CustomerPortalFileCategoryEnum.DiagnosesTech]: "Diagnostics techniques",
  [CustomerPortalFileCategoryEnum.Sale]: "Acquisition de mon bien",
  [CustomerPortalFileCategoryEnum.CoOwnershipTransfer]: "Vente de mon bien",
  [CustomerPortalFileCategoryEnum.Contract]: "Contrats fournisseurs",
  [CustomerPortalFileCategoryEnum.SignedManagementMandate]: "Mandat de gestion",
  [CustomerPortalFileCategoryEnum.AdministrativeDecisions]:
    "Décisions administratives",
};

type DocumentCategoryDescriptionFunction = (
  isLandlord?: boolean,
) => string | null;

export const DocumentCategoryDescription: {
  [key in CustomerPortalFileCategoryEnum]: DocumentCategoryDescriptionFunction | null;
} = {
  [CustomerPortalFileCategoryEnum.Litigation]: null,
  [CustomerPortalFileCategoryEnum.Sinister]: null,
  [CustomerPortalFileCategoryEnum.AccountSituation]: () =>
    "Situation de votre compte, vous pouvez à tout moment demander de l'éditer auprès de votre gestionnaire",
  [CustomerPortalFileCategoryEnum.Repair]: () =>
    "L'ensemble des documents concernant des interventions",
  [CustomerPortalFileCategoryEnum.Correspondence]: () =>
    "Documents partagés par votre gestionnaire",
  [CustomerPortalFileCategoryEnum.LessorCoOwnershipDocuments]: () =>
    "Les documents transmis par votre syndic de votre copropriété",
  [CustomerPortalFileCategoryEnum.MissionPropertyRepair]: () =>
    "Les documents liés aux travaux réalisés sur mon bien",
  [CustomerPortalFileCategoryEnum.Contract]: () =>
    "Contrats en cours sur l'immeuble avec les fournisseurs",
  [CustomerPortalFileCategoryEnum.Insurance]: () =>
    "Contrat d'assurance habitation",
  [CustomerPortalFileCategoryEnum.ProvisionCalls]: () =>
    "Derniers appels de provisions sur charges et/ou réparations",
  [CustomerPortalFileCategoryEnum.BoilerCertificate]: () =>
    "Contrat d'entretien de votre chauffage",
  [CustomerPortalFileCategoryEnum.BankReconciliation]: () =>
    "Relevés des comptes bancaires des comptes de la copropriété",
  [CustomerPortalFileCategoryEnum.TrusteeContract]: () =>
    "Contrat pour votre copropriété",
  [CustomerPortalFileCategoryEnum.Deposit]: () =>
    "Mes derniers acomptes versés",
  [CustomerPortalFileCategoryEnum.DiagnosesGl]: () =>
    "Ensemble des diagnostics immobiliers obligatoires réalisés sur mon bien",
  [CustomerPortalFileCategoryEnum.DueNotice]: () =>
    "Derniers avis d'échéances (appels de loyer de charges locatives) et quittances de loyer",
  [CustomerPortalFileCategoryEnum.GeneralAssembly]: () =>
    "Convocation, procès-verbaux,.. l'ensemble de vos documents concernant l'assemblée générale de votre copropriété",
  [CustomerPortalFileCategoryEnum.PeriodGeneralAssembly]: null,
  [CustomerPortalFileCategoryEnum.LandlordPropertyTaxes]: (isLandlord) =>
    isLandlord
      ? "Ajoutez et retrouvez vos avis de taxe foncière"
      : "Retrouvez ici les documents relatifs à la taxe foncière de votre bien",
  [CustomerPortalFileCategoryEnum.HouseholdWasteTax]: () =>
    "Taxe annuelle à ma charge pour l'enlèvement des ordures ménagères",
  [CustomerPortalFileCategoryEnum.CoOwnershipIdentificationNumber]: () =>
    "Immatriculation de mon immeuble au registre des copropriétés",
  [CustomerPortalFileCategoryEnum.Invoices]: () => "L'ensemble de vos factures",
  [CustomerPortalFileCategoryEnum.MaintenanceBook]: () =>
    "Carnet d'entretien de votre copropriété",
  [CustomerPortalFileCategoryEnum.LandIncomes]: () =>
    "Document d'aide à la déclaration de mes revenus fonciers",
  [CustomerPortalFileCategoryEnum.Leases]: () => "Contrat de location",
  [CustomerPortalFileCategoryEnum.OperatingLeaseReport]: () =>
    "Résumé des dépenses et encaissements liés à mon bien",
  [CustomerPortalFileCategoryEnum.RcpSettlement]: () =>
    "Règlement d'origine, modificatifs et plans de la copropriété",
  [CustomerPortalFileCategoryEnum.RegularizationRentalManagement]: () =>
    "Chaque année, le montant des charges locatives provisionnées chaque mois est comparé aux dépenses réelles.",
  [CustomerPortalFileCategoryEnum.ExternalExpenseRegularization]: () =>
    "Documents pour effectuer la régularisation de charges locatives",
  [CustomerPortalFileCategoryEnum.StatementGeneralExpenses]: () =>
    "Récapitulatif des dépenses engagées pour la gestion de ma copropriété",
  [CustomerPortalFileCategoryEnum.RentReview]: () =>
    "Chaque année mon loyer évolue selon un indice de référence",
  [CustomerPortalFileCategoryEnum.TenantStatementOfAccount]: () =>
    "Récapitulatif de vos frais, les sommes qui vous sont dues et les éventuelles retenues sur votre dépôt de garantie",
  [CustomerPortalFileCategoryEnum.TenantSettlementAdditional]: () =>
    "Récapitulatif de vos frais, les sommes qui vous sont dues et les éventuelles retenues suite à votre arrêté de compte locataire complémentaire",
  [CustomerPortalFileCategoryEnum.ExpensesRepartition]: () =>
    "Régularisation de charges courantes annuelles et régularisations de charges pour les travaux votés en assemblée générale",
  [CustomerPortalFileCategoryEnum.CouncilMeetingReport]: () =>
    "Compte-rendu des rencontres entre votre gestionnaire et vos représentants du conseil syndical",
  [CustomerPortalFileCategoryEnum.ReportVisit]: () =>
    "Observations constatées lors de la dernière visite d'immeuble et actions à mener",
  [CustomerPortalFileCategoryEnum.ResidentialRentalInspection]: () =>
    "États des lieux d’entrée et de sortie réalisés pour mon bien",
  [CustomerPortalFileCategoryEnum.Mandate]: null,
  [CustomerPortalFileCategoryEnum.MandateDebit]: null,
  [CustomerPortalFileCategoryEnum.ServiceOrder]: null,
  [CustomerPortalFileCategoryEnum.ElectroRegistredMailSubscription]: null,
  [CustomerPortalFileCategoryEnum.DiagnosesTech]: () =>
    "Ensemble des diagnostics techniques réalisés sur mes biens",
  [CustomerPortalFileCategoryEnum.Sale]: () =>
    "Retrouvez-ici toutes les informations sur votre bien",
  [CustomerPortalFileCategoryEnum.CoOwnershipTransfer]: null,
  [CustomerPortalFileCategoryEnum.SignedManagementMandate]: () =>
    "Votre contrat",
  [CustomerPortalFileCategoryEnum.AdministrativeDecisions]: () =>
    "Décisions administratives",
};

export const DocumentCategoryIcon: {
  [key in CustomerPortalFileCategoryEnum]: IconName;
} = {
  [CustomerPortalFileCategoryEnum.AccountSituation]: "landIncomes",
  [CustomerPortalFileCategoryEnum.Sinister]: "sinister",
  [CustomerPortalFileCategoryEnum.Litigation]: "gavel",
  [CustomerPortalFileCategoryEnum.Repair]: "repair",
  [CustomerPortalFileCategoryEnum.Correspondence]: "correspondence",
  [CustomerPortalFileCategoryEnum.LessorCoOwnershipDocuments]:
    "coOwnershipDocuments",
  [CustomerPortalFileCategoryEnum.MissionPropertyRepair]: "construction",
  [CustomerPortalFileCategoryEnum.Contract]: "trusteeContract",
  [CustomerPortalFileCategoryEnum.Insurance]: "insurance",
  [CustomerPortalFileCategoryEnum.ProvisionCalls]: "provisionCalls",
  [CustomerPortalFileCategoryEnum.BoilerCertificate]: "boilerCertificate",
  [CustomerPortalFileCategoryEnum.BankReconciliation]: "bankReconciliation",
  [CustomerPortalFileCategoryEnum.TrusteeContract]: "trusteeContract",
  [CustomerPortalFileCategoryEnum.Deposit]: "deposit",
  [CustomerPortalFileCategoryEnum.DiagnosesGl]: "diagnostic",
  [CustomerPortalFileCategoryEnum.DueNotice]: "dueNotice",
  [CustomerPortalFileCategoryEnum.GeneralAssembly]: "generalAssembly",
  [CustomerPortalFileCategoryEnum.PeriodGeneralAssembly]: "generalAssembly",
  [CustomerPortalFileCategoryEnum.HouseholdWasteTax]: "householdWasteTax",
  [CustomerPortalFileCategoryEnum.CoOwnershipIdentificationNumber]:
    "trusteeContract",
  [CustomerPortalFileCategoryEnum.Invoices]: "invoices",
  [CustomerPortalFileCategoryEnum.MaintenanceBook]: "construction",
  [CustomerPortalFileCategoryEnum.LandIncomes]: "landIncomes",
  [CustomerPortalFileCategoryEnum.Leases]: "leases",
  [CustomerPortalFileCategoryEnum.OperatingLeaseReport]: "operatingLeaseReport",
  [CustomerPortalFileCategoryEnum.RcpSettlement]: "reglementDeCoownership",
  [CustomerPortalFileCategoryEnum.RegularizationRentalManagement]:
    "regularizationRentalManagement",
  [CustomerPortalFileCategoryEnum.ExternalExpenseRegularization]:
    "regularizationRentalManagement",
  [CustomerPortalFileCategoryEnum.StatementGeneralExpenses]: "budget",
  [CustomerPortalFileCategoryEnum.RentReview]: "rentReview",
  [CustomerPortalFileCategoryEnum.TenantStatementOfAccount]: "pieChart",
  [CustomerPortalFileCategoryEnum.TenantSettlementAdditional]: "pieChart",
  [CustomerPortalFileCategoryEnum.ExpensesRepartition]: "expensesRepartition",
  [CustomerPortalFileCategoryEnum.CouncilMeetingReport]: "councilMeetingReport",
  [CustomerPortalFileCategoryEnum.ReportVisit]: "building",
  [CustomerPortalFileCategoryEnum.ResidentialRentalInspection]:
    "residentialRentalInspection",
  [CustomerPortalFileCategoryEnum.Mandate]: "mandate",
  [CustomerPortalFileCategoryEnum.MandateDebit]: "mandate",
  [CustomerPortalFileCategoryEnum.ServiceOrder]: "trusteeContract",
  [CustomerPortalFileCategoryEnum.ElectroRegistredMailSubscription]: "computer",
  [CustomerPortalFileCategoryEnum.DiagnosesTech]: "diagnostic",
  [CustomerPortalFileCategoryEnum.Sale]: "accord",
  [CustomerPortalFileCategoryEnum.CoOwnershipTransfer]: "trusteeContract",
  [CustomerPortalFileCategoryEnum.SignedManagementMandate]: "handleMandate",
  [CustomerPortalFileCategoryEnum.LandlordPropertyTaxes]: "bankReconciliation",
  [CustomerPortalFileCategoryEnum.AdministrativeDecisions]: "trusteeContract",
};

export const FileCategoryLabel: { [key in FileCategoryEnum]: string } = {
  [FileCategoryEnum.AccountSituation]: "Situation de compte",
  [FileCategoryEnum.DeficiencyMinutes]: "PV de carence assemblée générale",
  [FileCategoryEnum.Sinister]: "Sinistre",
  [FileCategoryEnum.Litigation]: "Contentieux",
  [FileCategoryEnum.AccountStatus]: "Situation de compte",
  [FileCategoryEnum.DatedState]: "État daté",
  [FileCategoryEnum.AccountingBalance]: "Balance comptable",
  [FileCategoryEnum.AccountingJournalReglement]: "Compta journal règlement",
  [FileCategoryEnum.AccountingJournalRentIssuance]:
    "Compta journal quittancement",
  [FileCategoryEnum.AccountingJournaux]: "Compta journaux",
  [FileCategoryEnum.AccountingLedgerAccount]: "Compta grand livre",
  [FileCategoryEnum.OtherCoOwner]: "Divers",
  [FileCategoryEnum.AccountingLinesCsv]: "",
  [FileCategoryEnum.AccountingPeriodAppendixes]: "Exercice comptable Annexe",
  [FileCategoryEnum.AccountingPointeCollateralie]: "Compta pointe garantie",
  [FileCategoryEnum.Annexe1]: "Annexe 1",
  [FileCategoryEnum.Annexe1Bis]: "Annexe 1bis",
  [FileCategoryEnum.Annexe2]: "Annexe 2",
  [FileCategoryEnum.Annexe3]: "Annexe 3",
  [FileCategoryEnum.Annexe4]: "Annexe 4",
  [FileCategoryEnum.Annexe5]: "Annexe 5",
  [FileCategoryEnum.AgCover]: "",
  [FileCategoryEnum.AppendixBalanceAccountant]: "",
  [FileCategoryEnum.AppendixReconciliationBank]: "",
  [FileCategoryEnum.Rgdd]: "Annexe RGDD",
  [FileCategoryEnum.CostInvoice]: "",
  [FileCategoryEnum.RgddN]: "Annexe_N RGDD",
  [FileCategoryEnum.RgddN1]: "Annexe_N1 RGDD",
  [FileCategoryEnum.RgddRepair]: "",
  [FileCategoryEnum.AnnexeFt]: "Annnexe FT",
  [FileCategoryEnum.AppendixLedgerAccount]: "",
  [FileCategoryEnum.RepairInvoice]: "Facture réparation",
  [FileCategoryEnum.AppendixArchive]: "",
  [FileCategoryEnum.AttendanceSheetFinalSigned]: "",
  [FileCategoryEnum.AttendanceSheetFinal]: "",
  [FileCategoryEnum.AttendanceSheetSigned]: "",
  [FileCategoryEnum.AttendanceSheet]: "",
  [FileCategoryEnum.AvisImposition]: "",
  [FileCategoryEnum.AwsTest]: "Aws test",
  [FileCategoryEnum.BankOrder]: "Ordre bancaire",
  [FileCategoryEnum.BankStatement]: "Relevés Bancaires",
  [FileCategoryEnum.CheckingBankStatement]: "Relevés Comptes courants",
  [FileCategoryEnum.SavingBankStatement]: "Relevés Comptes d'épargne",
  [FileCategoryEnum.CheckRemittanceNote]: "Bordereaux remise chèque",
  [FileCategoryEnum.Budget]: "Budget",
  [FileCategoryEnum.PayChecks]: "Bulletin de salaire",
  [FileCategoryEnum.CafAlFile]: "",
  [FileCategoryEnum.CandidateTenant]: "Candidat locataire",
  [FileCategoryEnum.MaintenanceBook]: "Carnet d'entretien",
  [FileCategoryEnum.BoilerCertificate]:
    "Attestation d'entretien du système de chauffage",
  [FileCategoryEnum.Check]: "Chèque",
  [FileCategoryEnum.LetterCheck]: "Lettre Chèque",
  [FileCategoryEnum.BankReconciliation]: "Rapprochement des comptes bancaires",
  [FileCategoryEnum.CompteReconciliationBank]: "Compte rapprochement bancaire",
  [FileCategoryEnum.Contract]: "Contrat",
  [FileCategoryEnum.GuardiansContract]: "Contrat gardiens",
  [FileCategoryEnum.TrusteeContract]: "Contrat syndic",
  [FileCategoryEnum.ContractorConstatimmo]: "",
  [FileCategoryEnum.Convocation]: "Convocation assemblée générale",
  [FileCategoryEnum.Deposit]: "Acomptes",
  [FileCategoryEnum.Diagnoses]: "Autre diagnostic",
  [FileCategoryEnum.DiagnosesGl]: "Diagnostics gl",
  [FileCategoryEnum.DiagnosesAsbestos]: "Diagnostic amiante",
  [FileCategoryEnum.DiagnosesPlumb]: "Diagnostic plomb",
  [FileCategoryEnum.DiagnosesEnergy]: "Diagnostic energétique",
  [FileCategoryEnum.DiagnosesParasiticState]: "Diagnostic état parasitaire",
  [FileCategoryEnum.DiagnosesGlobalTechnical]:
    "Diagnostic technique global (DTG)",
  [FileCategoryEnum.DiagnosesElevator]: "Diagnostic ascenseur",
  [FileCategoryEnum.DiagnosesOccupationalRisks]:
    "Diagnostic des risques professionnels",
  [FileCategoryEnum.DiagnosticSigne]: "Diagnostics lot",
  [FileCategoryEnum.DiagnosticInvoice]: "",
  [FileCategoryEnum.OthersLessors]: "Divers propriétaire",
  [FileCategoryEnum.OthersCoOwnership]: "Doc copropriété",
  [FileCategoryEnum.DueNotice]: "Avis d'échéance",
  [FileCategoryEnum.DueNoticeRent]: "Avis d'échéance loyer",
  [FileCategoryEnum.Decree]: "",
  [FileCategoryEnum.EdenredTransfersFile]: "",
  [FileCategoryEnum.EntryUnitAssesment]: "État des lieux entrée",
  [FileCategoryEnum.FeesConsolidationTransfer]: "",
  [FileCategoryEnum.FinancialPlan]: "Plan de financement",
  [FileCategoryEnum.Finding]: "Constat",
  [FileCategoryEnum.FundsCall]: "Appels de provisions",
  [FileCategoryEnum.GeneralAssembly]: "Assemblée générale",
  [FileCategoryEnum.GeneralAssemblyCertificate]:
    "Certificat d'assemblée générale",
  [FileCategoryEnum.GeneralAssemblyNotRepresentativeCoOwnerIdentity]: "",
  [FileCategoryEnum.LandlordPropertyTaxes]: "Taxes foncières",
  [FileCategoryEnum.HouseholdWasteTax]: "Taxe ordures ménagères",
  [FileCategoryEnum.CoOwnershipIdentificationNumber]: "Immatriculation copro",
  [FileCategoryEnum.IncomingMail]: "",
  [FileCategoryEnum.InsuranceOwner]: "Assurance propriétaire",
  [FileCategoryEnum.InsuranceTenant]: "Assurance locataires",
  [FileCategoryEnum.Invoices]: "Factures",
  [FileCategoryEnum.InvoiceFee]: "Honoraires",
  [FileCategoryEnum.JustificatifDivers]: "Justificatif divers",
  [FileCategoryEnum.JustificatifDomicile]: "Justificatif de domicile",
  [FileCategoryEnum.JustificatifIdentity]: "Justiticatif d'identité",
  [FileCategoryEnum.JustificatifIndemnites]: "Justificatif d'indemnités",
  [FileCategoryEnum.TenantLeaves]: "Justificatif de préavis",
  [FileCategoryEnum.JustificatifProfessionnel]: "Justificatif professionnel",
  [FileCategoryEnum.JustificatifTravail]: "Justificatif de travail",
  [FileCategoryEnum.JustificatifUnitsTransfer]: "",
  [FileCategoryEnum.KyribaFile]: "",
  [FileCategoryEnum.EdenredVirtualIban]: "",
  [FileCategoryEnum.LandIncomes]: "Déclaration de revenus fonciers",
  [FileCategoryEnum.Leases]: "Bail",
  [FileCategoryEnum.LeaseDivers]: "Divers locataire",
  [FileCategoryEnum.LeaseOtherDocument]: "Autre document de bail",
  [FileCategoryEnum.LeaseGuaranteeCommitment]: "Cautionnement Garant",
  [FileCategoryEnum.LedgerAccount]: "Grand livre",
  [FileCategoryEnum.MailStamping]: "",
  [FileCategoryEnum.MinutesSigned]: "Procès verbal AG (signé)",
  [FileCategoryEnum.MinutesSignedGl]: "PV signé",
  [FileCategoryEnum.ConvocationGl]: "Convocation",
  [FileCategoryEnum.OtherLessorCoOwnerShip]: "Autre",
  [FileCategoryEnum.Minutes]: "Procès verbal AG",
  [FileCategoryEnum.MissionAttachment]: "Mission attachement",
  [FileCategoryEnum.MoveOutInspection]: "État des lieux sortie",
  [FileCategoryEnum.MultiriskInsurance]: "",
  [FileCategoryEnum.Transfert]: "Avis de mutation",
  [FileCategoryEnum.OperatingLeaseReport]: "CRG",
  [FileCategoryEnum.OperatingLeaseReportCoownership]: "CRG Copropriétaire",
  [FileCategoryEnum.LessorRentalSituation]: "Etat locatif",
  [FileCategoryEnum.Organigrammes]: "Organigrammes",
  [FileCategoryEnum.OsDmdvDi]: "Os dmdv di",
  [FileCategoryEnum.OutgoingMail]: "Courrier sortant",
  [FileCategoryEnum.OutgoingMailAck]: "",
  [FileCategoryEnum.OutgoingMailScannedAck]: "",
  [FileCategoryEnum.PackFraicheur]: "Pack fraicheur",
  [FileCategoryEnum.PagardimRomAccountingLines]: "",
  [FileCategoryEnum.PagardimSalAccountingLines]: "",
  [FileCategoryEnum.PagardimSalaries]: "Fiche de salaire Gardien",
  [FileCategoryEnum.Passerelle]: "",
  [FileCategoryEnum.PicturePreVisit]: "",
  [FileCategoryEnum.PicturePublication]: "Photo publication",
  [FileCategoryEnum.Plans]: "Plans",
  [FileCategoryEnum.CarteProfessionnelle]: "Carte professionnelle",
  [FileCategoryEnum.RcpModificatifs]: "Rcp modificatifs",
  [FileCategoryEnum.RcpSettlement]: "Règlement de copropriété",
  [FileCategoryEnum.RegistreSecurite]: "Registre sécurité",
  [FileCategoryEnum.RegularizationRentalManagement]:
    "Régularisation de charges locatives",
  [FileCategoryEnum.RelanceCoownership]: "Relance copro",
  [FileCategoryEnum.RelanceGl]: "Relance gl",
  [FileCategoryEnum.StatementGeneralExpenses]: "Relevé général de dépenses",
  [FileCategoryEnum.RenewLease]: "Renouvellement bail",
  [FileCategoryEnum.AvisRentReceiptRent]: "Quittance de loyer",
  [FileCategoryEnum.RentReview]: "Révision de loyer",
  [FileCategoryEnum.Repartition]: "",
  [FileCategoryEnum.ExpensesRepartition]: "Répartition des charges courantes",
  [FileCategoryEnum.RepartitionRepair]: "Répartition des charges travaux",
  [FileCategoryEnum.MeetingReport]: "Compte rendu de réunion",
  [FileCategoryEnum.CouncilMeetingReport]: "Compte rendu de réunion CS",
  [FileCategoryEnum.ReportVisit]: "Compte rendu visite",
  [FileCategoryEnum.CouncilReportVisit]: "Compte rendu visite CS",
  [FileCategoryEnum.ResidentialRentalInspection]: "État des lieux",
  [FileCategoryEnum.Resolution]: "Résolution",
  [FileCategoryEnum.Sample]: "Sample",
  [FileCategoryEnum.Mandate]: "Mandat de prélèvement",
  [FileCategoryEnum.MandateDebit]: "Mandat de prélèvement",
  [FileCategoryEnum.ServiceOrder]: "Ordre de service",
  [FileCategoryEnum.ShowCasePoster]: "",
  [FileCategoryEnum.SignedDepositEngagement]: "Engagement caution signée",
  [FileCategoryEnum.SmokeDetector]: "Détecteur de fumée",
  [FileCategoryEnum.StatementAmountDues]: "Relevé des sommes dues",
  [FileCategoryEnum.ElectroRegistredMailSubscription]: "Souscription à E-reco",
  [FileCategoryEnum.TenantStatementOfAccount]: "Arrêté de compte locataire",
  [FileCategoryEnum.TenantSettlementAdditional]:
    "Arrêté de compte locataire complémentaire",
  [FileCategoryEnum.WorkOrder]: "Ordre service",
  [FileCategoryEnum.Workday]: "",
  [FileCategoryEnum.Sale]: "Vente",
  [FileCategoryEnum.WelcomePack]: "Document de bienvenue",
  [FileCategoryEnum.TransfertNotification]: "Avis virement",
  [FileCategoryEnum.CashAdvancePermed]: "Avance de trésorerie permanente",
  [FileCategoryEnum.AccountingInvoices]: "Exercice comptable Factures",
  [FileCategoryEnum.PayingCustomers]: "",
  [FileCategoryEnum.AccontingControl]: "",
  [FileCategoryEnum.OthersGuardians]: "Divers Gardiens",
  [FileCategoryEnum.Request]: "Demande",
  [FileCategoryEnum.Quote]: "Devis",
  [FileCategoryEnum.DocumentExtraction]: "",
  [FileCategoryEnum.AccountingLinesExtraction]: "",
  [FileCategoryEnum.InvoiceMediaPayment]: "",
  [FileCategoryEnum.InvoiceStamp]: "",
  [FileCategoryEnum.BankOperationMediaPayment]: "",
  [FileCategoryEnum.SaleAgreement]: "Document Promesse de Vente",
  [FileCategoryEnum.GlobalDocument]: "",
  [FileCategoryEnum.GeneralAssemblyPostalVote]: "Vote par correspondance",
  [FileCategoryEnum.SummarySheet]: "Fiche synthèse",
  [FileCategoryEnum.SubrogationNoticeDraft]: "Quittance subrogative non signée",
  [FileCategoryEnum.SubrogationNoticeSigned]: "Quittance subrogative signée",
  [FileCategoryEnum.InsuranceQuote]: "",
  [FileCategoryEnum.NewMandateDocuments]: "",
  [FileCategoryEnum.RentalManagementMandateAppendix]: "",
  [FileCategoryEnum.RcpRcdCertificate]: "",
  [FileCategoryEnum.StepCreationStatement]: "",
  [FileCategoryEnum.InsuranceContract]: "",
  [FileCategoryEnum.SaleAgreementInvoice]: "Facture DPV",
  [FileCategoryEnum.SignedManagementMandate]: "Mandat de gestion signé",
  [FileCategoryEnum.MissionCreationWelcomeDocumentCoOwner]:
    "Document de bienvenue",
  [FileCategoryEnum.AccountingCollectiveLoan]: "Documents bancaires",
  [FileCategoryEnum.SupportingCollectiveLoan]:
    "Documents pièces justificatives",
  [FileCategoryEnum.Reminders]: "Relances",
  [FileCategoryEnum.TenantLeaveReminderTenantLevelTwo]: "Relance contentieux",
  [FileCategoryEnum.CommercialLeaseWithoutFeesReminderLevelOne]:
    "Relance contentieux",
  [FileCategoryEnum.CommercialLeaseWithFeesReminderLevelOne]:
    "Relance contentieux",
  [FileCategoryEnum.CommercialLeaseReminderLevelTwo]: "Relance contentieux",
  [FileCategoryEnum.CommercialLeaseReminderLevelThree]: "Relance contentieux",
  [FileCategoryEnum.CoownershipReminderLevelTwo]: "Relance contentieux",
  [FileCategoryEnum.CoownershipReminderLevelThree]: "Relance contentieux",
  [FileCategoryEnum.AdministrativeDecisions]: "Décisions administratives",
  [FileCategoryEnum.FrontReworkDecree]:
    "Injonction pour le ravalement de façades",
  [FileCategoryEnum.ProvisionalAdministration]:
    "Immeuble placé sous le régime de l'administration provisoire art 29-1",
  [FileCategoryEnum.AdHocProxy]: "Mandataire ad hoc art 29-1 A",
  [FileCategoryEnum.CommonAreasRiskDecree]:
    "Arrêté de péril les parties communes",
  [FileCategoryEnum.CommonEquipmentsRiskDecree]:
    "Arrêté de péril sur les équipements communs",
  [FileCategoryEnum.DefaultOrder]: "Ordonnance de carence",
  [FileCategoryEnum.LivingProhibitionDecree]: "Interdiction d'habiter",
  [FileCategoryEnum.ReworkDecree]: "Injonction de travaux",
  [FileCategoryEnum.UnsanitaryDecree]: "Déclaration d'insalubrité",
  [FileCategoryEnum.HasRebuildPlan]: "Plan de reconstruction",
  [FileCategoryEnum.CollectiveDpe]: "DPE collectif",
  [FileCategoryEnum.DiagnosesStructure]: "Diagnostic structure",
  [FileCategoryEnum.DiagnosesEnergyAudit]: "Audit énergétique",
  [FileCategoryEnum.DiagnosesSanitaryConnection]: "Diagnostic assainissement",
  [FileCategoryEnum.DiagnosesEnergyPerformanceContract]:
    "Contrat de performance énergétique",
  [FileCategoryEnum.MissionPropertyRepairPack]: "Pack travaux",
  [FileCategoryEnum.MissionPropertyRepairRenovationStudy]: "Etude Rénov+",
  [FileCategoryEnum.WorkSiteQuote]: "Devis validés",
  [FileCategoryEnum.MissionPropertyRepairConstructionDamageInsurance]:
    "Dommage Ouvrage",
  [FileCategoryEnum.MissionPropertyRepairWorksContract]: "Marchés travaux",
  [FileCategoryEnum.MissionPropertyRepairContractorInsurance]:
    "Assurance des entreprises",
  [FileCategoryEnum.MissionPropertyRepairProjectManagement]: "Maitrise d'œuvre",
  [FileCategoryEnum.MissionPropertyRepairHealthSafetyCoordination]: "SPS",
  [FileCategoryEnum.MissionPropertyRepairSiteReport]:
    "Compte rendu de chantier",
  [FileCategoryEnum.MissionPropertyRepairAcceptanceReport]: "PV de réception",
  [FileCategoryEnum.MissionPropertyRepairReservationLifting]:
    "Levée de réserve",
  [FileCategoryEnum.MissionPropertyRepairOther]: "Divers",
};

export const E_RECO_STATUSES_MAPPING: Partial<Record<MailStatusEnum, string>> =
  {
    [MailStatusEnum.ErecoRead]: "Lu",
    [MailStatusEnum.ErecoNotified]: "Non lu",
    [MailStatusEnum.ErecoExpired]: "Expiré",
  };
